<template>
    <div class="loader-container">
        <!-- Centered middle section -->
        <div class="middle-content">
            <transition-group
                v-if="!error && visibleSteps.length"
                name="overlap-fade"
                tag="div"
                class="loading-state"
            >
                <div
                    v-for="(step, index) in visibleSteps"
                    :key="step?.id ?? index"
                    :class="['loading-step', 'step-' + index]"
                >
                    <Icon icon-name="ai-sparkling-icon" />
                    <span class="step-text">{{ step?.text }}</span>
                </div>
            </transition-group>
            <div class="error-text-wrapper" v-else>
                <div class="error-message">
                    <Icon icon-name="info" /> Server issue. Please try again
                </div>
            </div>
        </div>

        <!-- Disclaimer fixed to the bottom -->
        <div class="disclaimer-wrapper">
            <div class="error-buttons-wrapper">
                <TertiaryButton v-if="stop && !error" dark-scheme type="subtle" @click="stop" icon="ai-stop-icon" text="Stop" />
                <TertiaryButton v-if="error" dark-scheme type="subtle" @click="close" icon="close" text="Close generator" />
                <TertiaryButton v-if="error" dark-scheme type="subtle" @click="retry" icon="rotate" text="Retry" />
            </div>
            <div class="text">{{disclaimer}}</div>
        </div>
    </div>
</template>

<script>
import TertiaryButton from "@/scripts/common/components/TertiaryButton.vue";
import Icon from "@/scripts/common/Icon.vue";

export default {
    name: "AILoader",
    props: ['loadSteps', 'visibleStepsNum', 'error', 'close', 'retry', 'stop', 'disclaimer'],
    components: {
        Icon,
        TertiaryButton
    },
    data() {
        return {
            visibleSteps: [],
            currentIndex: 3,
            timer: null,
        };
    },
    mounted() {
        if (!this.loadSteps || !this.loadSteps.length) {
            console.warn('AILoader: loadSteps is undefined or empty.');
            return;
        }
        // Initialize with the first visibleStepsNum items
        this.visibleSteps = this.loadSteps.slice(0, this.visibleStepsNum);
        this.currentIndex = this.visibleStepsNum;
        this.startLoop();
    },
    beforeUnmount() {
        this.stopLoop();
    },
    methods: {
        startLoop() {
            this.timer = setInterval(() => {
                const nextStep = this.loadSteps[this.currentIndex];
                if (nextStep) {
                    this.visibleSteps = [...this.visibleSteps.slice(1), nextStep];
                }
                this.currentIndex = (this.currentIndex + 1) % this.loadSteps.length;
            }, 1000);
        },
        stopLoop() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.loader-container {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: var(--asphalt100);
    padding: 24px;

    .middle-content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .loading-state {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 310px;
    }

    .error-text-wrapper {
        .error-message {
            color: var(--rose50);
            font: var(--ai-ui-h3);
            line-height: 30.741px;
            display: flex;
            align-items: center;
            gap: 12px;

            svg {
                width: 24px;
                height: 24px;
                aspect-ratio: 1/1;
                color: var(--rose50);
            }
        }
    }

    .loading-step {
        height: 48px;
        display: flex;
        align-items: center;
        gap: 12px;
        font: var(--ai-ui-h3);
        color: var(--asphalt50);
        background: transparent;
        transition: transform 1s ease;

        &.step-1 {
            .step-text {
                color: var(--asphalt10);
            }
            svg {
                color: var(--asphalt10);
            }
        }
    }

    .step-text,
    svg {
        transition: color 1s ease-in-out;
        color: var(--asphalt50);
    }

    .disclaimer-wrapper {
        position: absolute;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 16px;

        .error-buttons-wrapper {
            display: flex;
            align-items: flex-start;
            gap: 12px;
        }

        .text {
            text-align: center;
            color: var(--asphalt40);
            font: var(--editor-ui-b3-regular);
        }
    }
}

.overlap-fade-enter-active {
    transition: transform 1s ease, opacity 1s ease;
}
.overlap-fade-enter-from {
    transform: translateY(100%);
    opacity: 0;
}
.overlap-fade-enter-to {
    transform: translateY(0);
    opacity: 1;
}

.overlap-fade-leave-active {
    position: absolute;
    width: 100%;
    z-index: 2;
    background: transparent !important;
    transition: opacity 1s ease !important;
    transition-property: opacity;
}
.overlap-fade-leave-from {
    opacity: 1;
    transform: none;
}
.overlap-fade-leave-to {
    opacity: 0;
    transform: none;
}

.overlap-fade-move {
    transition: transform 1s ease;
}
</style>
